import { Match, Show, Switch, useContext } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import clsx from 'clsx';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { User } from '../../../types/shared';
import { UserOrderHistory } from '../../order-history/user-order-history';
import { AccountEducationContent } from '../../account-education-content/account-education-content';
import { MyContent } from '../../my-content/my-content';
import { GraduationCapIcon } from '../../icons-library/graduation-cap';
import { HeartIcon } from '../../icons-library/heart';
import { PdfBuilderIcon } from '../../icons-library/pdf-builder-icon';
import { AccountView } from '../../../tools/account-slugs';
import { Section } from '../../../grid-system/section/section';
import { Grid } from '../../../grid-system/grid/grid';
import { Heading } from '../../../ui-components/heading/heading';
import { Text } from '../../../ui-components/text/text';
import { cviSemantic } from '../../../style/theme';

export type UserProfileDesktopProps = {
    user: User | null;
    view: AccountView;
    handleSignOutClick: () => void;
};

export const UserProfileDesktop = (props: UserProfileDesktopProps) => {
    const {
        localize,
        siteInfo,
        coursesListingPage,
        pdfBuilderPage,
        translations,
        userProfileUrls,
    } = useContext(AppContext);

    const navigate = useNavigate();

    const navItemClasses = (active?: boolean) => clsx(
        'flex items-center gap-cvi-5 w-fit text-left no-underline',
        'body-md font-cvi-regular cursor-pointer bg-transparent border-0 p-0',
        'transition-colors duration-200',
        active
            ? 'text-cvi-fg-link hover:text-cvi-fg-brand'
            : 'text-cvi-fg-default hover:text-cvi-fg-link',
    );

    const SectionHeader = (p: { children: string }) => (
        <Heading tag="h3" variant="heading-xl" noBlockSpacing>
            {p.children}
        </Heading>
    );

    const Sidebar = () => (
        <aside class="flex flex-col pt-cvi-9 pb-cvi-9">
            {/* Greeting banner */}
            <div class="border-b border-cvi-border-muted py-cvi-4 mb-cvi-9">
                <Heading tag="h1" variant="heading-xl" simpleColor={cviSemantic.fgBrand} noBlockSpacing>
                    {`${localize('profile-greeting', 'Hi')} ${props.user?.firstName || ''}`}
                </Heading>
            </div>

            <Show when={siteInfo.siteType === 'default'}>
                <nav class="flex flex-col gap-cvi-10">
                    <section class="flex flex-col gap-cvi-8">
                        <SectionHeader>{localize('my-content', 'My content')}</SectionHeader>
                        <ul class="flex flex-col gap-cvi-4 list-none p-0 m-0">
                            <Show when={coursesListingPage.url}>
                                <li>
                                    <A
                                        href={userProfileUrls.learning}
                                        class={navItemClasses(props.view === 'learning')}
                                        aria-current={props.view === 'learning' ? 'page' : undefined}
                                    >
                                        <GraduationCapIcon width={2.55} height={2.55} ariaHidden />
                                        <span>{localize('e-learning', 'E-learning')}</span>
                                    </A>
                                </li>
                            </Show>
                            <li>
                                <A
                                    href={userProfileUrls.bookmarks}
                                    class={navItemClasses(props.view === 'bookmarks')}
                                    aria-current={props.view === 'bookmarks' ? 'page' : undefined}
                                >
                                    <HeartIcon width={2.55} height={2.55} ariaHidden />
                                    <span>{localize('bookmarks', 'Bookmarks')}</span>
                                </A>
                            </li>
                        </ul>
                    </section>

                    <Show when={pdfBuilderPage.url}>
                        <section class="flex flex-col gap-cvi-8">
                            <SectionHeader>{localize('profile-tools', 'Tools')}</SectionHeader>
                            <ul class="flex flex-col gap-cvi-4 list-none p-0 m-0">
                                <li>
                                    <button
                                        class={navItemClasses()}
                                        onClick={() => navigate(pdfBuilderPage.url)}
                                    >
                                        <PdfBuilderIcon width={2.55} height={2.55} ariaHidden />
                                        <span>{translations['profile-pdf-builder-heading'] || pdfBuilderPage.title}</span>
                                    </button>
                                </li>
                            </ul>
                        </section>
                    </Show>
                </nav>
            </Show>

            {/* Signed-in card pinned to viewport bottom while sidebar is in view. */}
            <div class="mt-auto sticky bottom-cvi-6 bg-cvi-bg-block-neutral rounded-cvi-md p-cvi-6 flex flex-col gap-cvi-2">
                <Text fontSize="body-xs" color={cviSemantic.fgMuted} noBlockSpacing class="font-cvi-medium">
                    {`${localize('signed-in-as', 'Signed in as')}:`}
                </Text>
                <div title={props.user?.email}>
                    <Text fontSize="body-sm" noBlockSpacing>
                        {[ props.user?.firstName, props.user?.lastName ].filter(Boolean).join(' ') || props.user?.email || ''}
                    </Text>
                </div>
                <button
                    class="link-as-button self-start body-sm font-cvi-regular bg-transparent border-0 p-0 mt-cvi-2"
                    onClick={() => props.handleSignOutClick()}
                >
                    {localize('sign-out', 'Sign out')}
                </button>
            </div>
        </aside>
    );

    return (
        <Section
            templateShorthand={[12]}
            widthType="bgFull"
            customCss={'padding-top: 0; padding-bottom: 0; min-height: 50dvh;'}
        >
            <Grid
                templateShorthand={[3, 9]}
                responsive={{
                    largeDesktop: [3, 9],
                    smallDesktop: [3, 9],
                    tablet: [12],
                    mobile: [12],
                }}
            >
                <Sidebar />
                <div class="min-w-0 pt-cvi-9 pb-cvi-12">
                    <Switch>
                        <Match when={props.view === 'bookmarks'}>
                            <MyContent />
                        </Match>
                        <Match when={props.view === 'learning'}>
                            <AccountEducationContent />
                        </Match>
                        <Match when={props.view === 'orders'}>
                            <UserOrderHistory />
                        </Match>
                    </Switch>
                </div>
            </Grid>
        </Section>
    );
};
